import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAPIContext } from './api';
import { useAuthContext } from './auth';
import { BrandsAPI } from '../../../constructs/brands/interface';
import { InstagramAPI } from '../../../constructs/instagram/interface';
import { CreativityContentData } from '../types/banner';

import { sortBanners } from '../utils';

const ENDPOINT = '/banners';

const DEFAULT_PAGE_LIMIT = 60;
// El dashboard solo muestra los más recientes, una sola página chica alcanza.
const DASHBOARD_PAGE_LIMIT = 24;

export interface PaginatedBanners {
  items: CreativityContentData[];
  nextToken?: string;
}

export interface GetBannersOptions {
  limit?: number;
  nextToken?: string;
}

interface BannersContextType {
  allBanners: CreativityContentData[];
  isRefreshingAll: boolean;
  folders: string[];

  getBannersByFolder: (folderPath?: string, opts?: GetBannersOptions) => Promise<PaginatedBanners>;
  refreshAllBanners: () => Promise<void>;
  // Dispara el re-scrape de reels del cliente (handle principal) vía Apify. Encola
  // el trabajo y vuelve enseguida; los reels nuevos aparecen en el library al cabo
  // de unos segundos (procesamiento async). Lanza si el endpoint falla (no silencia).
  refreshReels: () => Promise<void>;
  loadFolders: () => Promise<string[]>;
  // Registra banners ya cargados (p.ej. páginas del library) para que el modal global
  // pueda resolverlos por id aunque no estén en la primera página de allBanners.
  registerBanners: (banners: CreativityContentData[]) => void;
  findBannerById: (bannerId: string) => CreativityContentData | null;
}

const BannersContext = createContext<BannersContextType | undefined>(undefined);

export const BannersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { callEndpoint } = useAPIContext();
  const { session } = useAuthContext();

  const hasInitialized = useRef(false);

  const [allBanners, setAllBanners] = useState<CreativityContentData[]>([]);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const [folders, setFolders] = useState<string[]>([]);

  // Registro de banners conocidos (id -> banner) para resolver el modal global desde cualquier página.
  const knownBanners = useRef<Map<string, CreativityContentData>>(new Map());

  const registerBanners = useCallback((banners: CreativityContentData[]) => {
    banners.forEach(banner => {
      knownBanners.current.set(banner.bannerId, banner);
    });
  }, []);

  const findBannerById = useCallback((bannerId: string): CreativityContentData | null => {
    return knownBanners.current.get(bannerId) ?? null;
  }, []);

  const loadFolders = useCallback(async (): Promise<string[]> => {
    if (!session) return [];

    try {
      const result = await callEndpoint<BrandsAPI, 'list-folders'>('brands', '/folders', 'GET');
      setFolders(result);
      return result;
    } catch (error) {
      return [];
    }
  }, [session, callEndpoint]);

  const getBannersByFolder = useMemo(() => {
    return async (folderPath?: string, opts?: GetBannersOptions): Promise<PaginatedBanners> => {
      if (!session) return { items: [] };

      try {
        const base = folderPath ? `${ENDPOINT}/${folderPath}` : ENDPOINT;

        const params = new URLSearchParams();
        params.set('limit', String(opts?.limit ?? DEFAULT_PAGE_LIMIT));
        if (opts?.nextToken) {
          params.set('nextToken', opts.nextToken);
        }

        const endpoint = `${base}?${params.toString()}`;
        const res = await callEndpoint<BrandsAPI, 'list-banners'>('brands', endpoint, 'GET');

        // reels/videos vienen del backend con `contentId` pero SIN `bannerId` (ese campo es
        // propio de las creativities). El resto de la app los resuelve por `bannerId` (abrir
        // el asset en el modal, toggle "Enviar a Social"), así que lo normalizamos a
        // `contentId` acá, en el único punto de entrada de los items.
        const items = res.items.map(it => ({
          ...it,
          bannerId: it.bannerId ?? (it as { contentId?: string }).contentId ?? ''
        }));

        // El servidor ya devuelve cada página ordenada (más nuevos primero). NO re-ordenamos
        // entre páginas para mantener consistente el append del scroll infinito.
        registerBanners(items);
        return { items, nextToken: res.nextToken };
      } catch (error) {
        return { items: [] };
      }
    };
  }, [callEndpoint, session, registerBanners]);

  const refreshReels = useCallback(async (): Promise<void> => {
    if (!session) return;
    // POST /instagram (sin widgetId): re-scrapea el handle principal del cliente.
    await callEndpoint<InstagramAPI, 'refresh-reels'>('instagram', '', 'POST');
  }, [session, callEndpoint]);

  const refreshAllBanners = useCallback(async () => {
    if (!session) return;

    setIsRefreshingAll(true);
    try {
      const result = await getBannersByFolder(undefined, { limit: DASHBOARD_PAGE_LIMIT });
      // El dashboard solo necesita los más recientes; ordenamos dentro de la página.
      setAllBanners(sortBanners(result.items));
    } finally {
      setIsRefreshingAll(false);
    }
  }, [session, getBannersByFolder]);

  useEffect(() => {
    if (session && !hasInitialized.current) {
      hasInitialized.current = true;
      refreshAllBanners();
      loadFolders();
    }

    // Si no hay sesión, resetear el flag
    if (!session) {
      hasInitialized.current = false;
      knownBanners.current.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const value: BannersContextType = {
    allBanners,
    folders,
    isRefreshingAll,
    getBannersByFolder,
    loadFolders,
    refreshAllBanners,
    refreshReels,
    registerBanners,
    findBannerById
  };

  return <BannersContext.Provider value={value}>{children}</BannersContext.Provider>;
};

export const useBanners = (): BannersContextType => {
  const context = useContext(BannersContext);
  if (context === undefined) {
    throw new Error('useBanners must be used within a BannersProvider');
  }
  return context;
};
